import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { ErrorBoundary } from 'react-error-boundary';
import App from './App';
import AppThemeProvider from './ThemeProvider';
import { UserProvider } from './contexts/UserContext';
import { AuthProvider } from './contexts/AuthContext';
import {
  acquireApiToken,
  establishSession,
  initializeMsal,
  msalInstance,
  type SessionResult,
} from './authConfig';
import { setTokenProvider } from './APIService';
import './index.css';
import './assets/less/main.less';

const ErrorFallback = ({ error }: { error: Error }) => (
  <div style={{ padding: '2rem', fontFamily: 'Inter, sans-serif' }}>
    <h1>Something went wrong</h1>
    <pre style={{ whiteSpace: 'pre-wrap' }}>{error.message}</pre>
  </div>
);

const root = () => document.getElementById('root') as HTMLElement;

const renderApp = (session: SessionResult) => {
  ReactDOM.createRoot(root()).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <AuthProvider initialSession={session}>
          <UserProvider>
            <AppThemeProvider>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </ErrorBoundary>
            </AppThemeProvider>
          </UserProvider>
        </AuthProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
};

/**
 * Auth is resolved before the first render, in this order:
 *
 *   1. `APIService` learns where to get a bearer token from.
 *   2. MSAL initialises and processes any pending redirect response.
 *   3. The token is acquired and its roles resolved, if there is an account.
 *   4. React renders, already knowing whether the user is signed in.
 *
 * Resolving the session here rather than in a post-render effect is what stops
 * the login screen flashing (or sticking) after a successful redirect.
 *
 * The outer try/catch is a last resort, not the primary error path — every
 * expected failure (a declined sign-in, a redirect B2C rejected) is caught
 * inside `authConfig.ts` and turned into a `SessionResult` the Login screen can
 * show with a Retry button. This only fires for the unexpected case, and its
 * job is narrow: make sure *something* renders. Without it, an exception
 * anywhere in this chain stops the `await` before `ReactDOM.createRoot` is ever
 * called — nothing in the page can catch that, not even the `ErrorBoundary`
 * below, because React never mounted. The result is a silent blank page that
 * looks exactly like "the sign-in button did nothing."
 */
const bootstrap = async () => {
  try {
    setTokenProvider(acquireApiToken);
    await initializeMsal();
    const session = await establishSession();
    renderApp(session);
  } catch (error) {
    console.error('App failed to start:', error);
    renderApp({
      status: 'error',
      error: 'The app failed to start. Please reload the page.',
      hasAppAccessGroup: false,
    });
  }
};

void bootstrap();
